


































































































































import { Component, Mixins, Prop } from 'vue-property-decorator'
import { namespace, State } from 'vuex-class'
import { __ } from 'bits/intl'
import { navigateTo, transformUrl } from 'bits/location'
import CollectionListItem from 'vuecomponents/collection-list-item.vue'
import OzBackButton from 'library/v4/components/OzBackButton.vue'
import OzBox, { OzBoxColors } from 'library/v4/components/OzBox.vue'
import OzButton from 'library/v4/components/OzButton.vue'
import OzCloseButton from 'library/v4/components/OzCloseButton.vue'
import OzDivider, { OzDividerColorScheme } from 'library/v4/components/OzDivider.vue'
import OzRichModal from 'library/v4/components/oz-rich-modal.vue'
import NeonMembershipTier, { Variant as NeonMembershipTierVariant } from 'vuecomponents/NeonMembershipTier.vue'
import PlatinumMembershipTier, {
  Variant as PlatinumMembershipTierVariant,
} from 'vuecomponents/PlatinumMembershipTier.vue'
import OzSimpleModal from 'vuecomponents/oz-simple-modal.vue'
import { Card } from '@padlet/arvo'
import { UpgradeSource, UpgradeStep } from 'vuexstore/modules/plans'
import { getPrettyCurrentDate } from 'bits/date'
import { User } from 'types'
import { AdvertisedQuotasResult } from 'types'
import { OzContainedButtonColorScheme } from '../library/v4/components/OzContainedButton.vue'

const browser = namespace('browser')
const experiments = namespace('experiments')
const plans = namespace('plans')

@Component({
  components: {
    CollectionListItem,
    NeonMembershipTier,
    OzBackButton,
    OzBox,
    OzButton,
    OzCloseButton,
    OzDivider,
    OzRichModal,
    OzSimpleModal,
    PlatinumMembershipTier,
  },
})
export default class BillingUpgradeModal extends Mixins() {
  @Prop() upgradeStep!: UpgradeStep
  @Prop() user!: User

  @State fileThatExceededSize!: any

  @browser.Getter isPhone!: boolean
  @browser.Getter windowHeight!: number
  @experiments.Getter isPlanVisualActive!: boolean
  @experiments.Getter isPlanVisualUsingNewBasicName!: boolean
  @experiments.Getter isPlanVisualUsingNewProName!: boolean
  @experiments.Getter isPlanVisualUsingNewColors!: boolean
  @plans.Action fetchPlans
  @plans.Getter plans
  @plans.Getter plansFetched?: boolean
  @plans.Getter advertisedQuotas!: AdvertisedQuotasResult
  @plans.Getter isLoadingAdvertisedQuotas!: boolean
  @plans.Getter upgradeSource!: UpgradeSource | null

  NeonMembershipTierVariant = NeonMembershipTierVariant
  PlatinumMembershipTierVariant = PlatinumMembershipTierVariant
  OzBoxColors = OzBoxColors
  OzDividerColorScheme = OzDividerColorScheme
  UpgradeStep = UpgradeStep

  currentDate = getPrettyCurrentDate()
  padletQuotaUpgradeSteps: UpgradeStep[] = [
    UpgradeStep.BelowPadletQuota,
    UpgradeStep.AtPadletQuota,
    UpgradeStep.OverPadletQuota,
  ]

  get paymentScheduleInfoText(): object {
    return {
      monthly: {
        title: this.__('Pay Monthly'),
        price: this.plans.platinum.monthly.price,
        buttonText: this.__('Continue'),
      },
      annual: {
        title: this.__('Pay Annually'),
        price: this.plans.platinum.annual.price,
        discount: this.__('Save %{annualSavingsPercentage}%', { annualSavingsPercentage: 20 }),
        buttonText: this.__('Continue'),
      },
    }
  }

  get upgradeStepTexts(): object {
    return {
      [UpgradeStep.BelowPadletQuota]: {
        headline: this.__('Upgrade to get the most out of Padlet.'),
      },
      [UpgradeStep.AtPadletQuota]: {
        headline: this.__('Upgrade your membership to continue making padlets.'),
      },
      [UpgradeStep.OverPadletQuota]: {
        headline: this.__('Upgrade your membership to continue making padlets.'),
      },
      [UpgradeStep.OverFileSizeQuota]: {
        headline: this.__('Upgrade your membership to upload bigger files.'),
      },
    }
  }

  get card(): Card | null {
    return null
    // Aug 2021 - Stop using the billing getter for this component because it was only being used to check
    // for an existing card, which currently will never happen for Basic users (the only users who will see
    // this modal). As a result the 'Step 2 of 2' of this modal will never be shown, but we may change the
    // backend in the future to provide a card for free users who previously had a web subscription.
    // return this.billing?.card
  }

  get showMembershipTiers(): boolean {
    return [...this.padletQuotaUpgradeSteps, UpgradeStep.OverFileSizeQuota].includes(this.upgradeStep)
  }

  get checkoutUrl(): string {
    const searchParams = new URLSearchParams({ upgrade_source: this.upgradeSource || '' })
    return this.upgradeStep === UpgradeStep.ConfirmProMonthly
      ? transformUrl(this.plans.platinum.monthly.url, { searchParams })
      : transformUrl(this.plans.platinum.annual.url, { searchParams })
  }

  get upgradeStartDateText(): string {
    return this.__(
      'You are upgrading to <strong>Pro</strong> membership starting now (<strong>%{startDate}</strong>)',
      {
        startDate: this.currentDate,
      },
    )
  }

  get recurringChargesText(): string {
    const selectedPlanCost =
      this.upgradeStep === UpgradeStep.ConfirmProMonthly
        ? this.plans.platinum.monthly.price
        : this.plans.platinum.annual.price

    return this.__('Your recurring charges will be <strong>%{costAmount}</strong> a %{billingSchedule}', {
      costAmount: selectedPlanCost,
      billingSchedule: this.upgradeStep === UpgradeStep.ConfirmProMonthly ? 'month' : 'year',
    })
  }

  get cardInfoText(): string {
    if (this.card) {
      const lastFourDigits = this.card.last4
      return this.__('We will charge your card ending in *%{fourEndingDigits} <strong>now</strong>', {
        fourEndingDigits: lastFourDigits,
      })
    }
    return ''
  }

  get currentUpgradeStepTexts() {
    return this.upgradeStepTexts[this.upgradeStep]
  }

  get fileSize(): number {
    return this.fileThatExceededSize ? this.fileThatExceededSize.size : 0
  }

  get neonMembershipTierVariant(): NeonMembershipTierVariant {
    switch (this.upgradeStep) {
      case UpgradeStep.OverPadletQuota:
        return NeonMembershipTierVariant.OverPadletQuotaOld
      case UpgradeStep.AtPadletQuota:
        return NeonMembershipTierVariant.AtPadletQuotaOld
      case UpgradeStep.OverFileSizeQuota:
        return NeonMembershipTierVariant.FileSizeQuotaOld
      default:
        return NeonMembershipTierVariant.UnderPadletQuotaOld
    }
  }

  get platinumExperimentalPlanName(): string {
    if (!this.isPlanVisualActive) return ''

    return this.isPlanVisualUsingNewProName ? this.__('Platinum') : this.__('Pro')
  }

  get platinumExperimentalBackgroundColor(): OzBoxColors | null {
    if (!this.isPlanVisualActive) return null
    return this.isPlanVisualUsingNewColors ? OzBoxColors.Green : OzBoxColors.Pink
  }

  get platinumExperimentalButtonColor(): OzContainedButtonColorScheme {
    if (!this.isPlanVisualActive) return OzContainedButtonColorScheme.Primary
    return this.isPlanVisualUsingNewColors ? OzContainedButtonColorScheme.Green : OzContainedButtonColorScheme.Primary
  }

  get neonExperimentalPlanName(): string {
    if (!this.isPlanVisualActive) return ''

    return this.isPlanVisualUsingNewBasicName ? this.__('Neon') : this.__('Basic')
  }

  get neonExperimentalBackgroundColor(): OzBoxColors | null {
    if (!this.isPlanVisualActive) return null
    return this.isPlanVisualUsingNewColors ? OzBoxColors.Pink : OzBoxColors.Secondary
  }

  get upgradeTitle(): string {
    if (!this.isPlanVisualActive) return this.__('Upgrade to Pro membership')
    return this.isPlanVisualUsingNewProName
      ? this.__('Upgrade to Platinum membership')
      : this.__('Upgrade to Pro membership')
  }

  changeUpgradeStep(nextStep): void {
    const searchParams = new URLSearchParams({ upgrade_source: this.upgradeSource || '' })
    if (nextStep === UpgradeStep.ConfirmProMonthly && !this.card) {
      navigateTo(transformUrl(this.plans.platinum.monthly.url, { searchParams }))
    } else if (nextStep === UpgradeStep.ConfirmProAnnual && !this.card) {
      navigateTo(transformUrl(this.plans.platinum.annual.url, { searchParams }))
    } else {
      this.$emit('change-upgrade-step')
    }
  }

  cancel(): void {
    this.$emit('cancel')
  }

  upgrade(): void {
    this.$emit('change-upgrade-step', UpgradeStep.SchedulePro)
  }

  created(): void {
    if (this.showMembershipTiers && !this.plansFetched) {
      this.fetchPlans()
    }
  }
}
